import { MinLengthArray, reject } from "./TypingUtils";
import { CompareFunc } from "./Comparers";
import { InvalidArgumentError } from "./Errors";

export function* reverseArray<T>(array: readonly T[]): IterableIterator<T> {
    for (let i = array.length - 1; i >= 0; i--) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        yield array[i]!;
    }
}

export function toAsyncIterator<T>(generator: () => AsyncGenerator<T>): AsyncIterableIterator<T> {
    return AsyncIterator.from(generator());
}

export function findLast<T>(items: Iterable<T>, condition: (item: T) => boolean): T | undefined {
    let result: T | undefined;
    for (const item of items) {
        if (condition(item)) {
            result = item;
        }
    }
    return result;
}

export function* concat<T>(...iterables: Array<Iterable<T>>): IterableIterator<T> {
    for (const iterableItem of iterables) {
        yield* iterableItem;
    }
}

export function* iterateValues<TK extends string | symbol | number, TV>(obj: { [key in TK]: TV }): Iterable<TV> {
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            yield obj[key];
        }
    }
}

export function iterateKeys<TO>(obj: Readonly<TO>): Iterable<Extract<keyof TO, string>>;
export function iterateKeys<TV>(obj: Readonly<{ [key: string]: TV }>): Iterable<string>;
export function iterateKeys(obj: Readonly<{}>): Iterable<string>;
export function* iterateKeys<TK extends string | symbol | number>(obj: { [key in TK]: unknown }): Iterable<TK> {
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            yield key;
        }
    }
}

export function iterateEntries<TO>(obj: Readonly<TO>): Iterable<[Extract<keyof TO, string>, TO[keyof TO]]>;
export function iterateEntries<TV>(obj: Readonly<{ [key: string]: TV }>): Iterable<[string, TV]>;
export function iterateEntries(obj: Readonly<{}>): Iterable<[string, unknown]>;
export function* iterateEntries<TV>(obj: Readonly<{ [key: string]: TV }>): Iterable<[string, TV]> {
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            yield [key, obj[key]!];
        }
    }
}

export function count<T>(items: Iterable<T>): number {
    let result = 0;
    for (const _item of items) {
        result++;
    }
    return result;
}

const EmptySingleElement = Symbol.for("EmptySingleElement");
const NoValueMarker = Symbol.for("NoValueMarker");

export class IterUtils {
    public static count = count;
    public static concat = concat;

    public static isEmpty<T>(iterable: Iterable<T>): boolean {
        for (const _item of iterable) {
            return false;
        }
        return true;
    }

    public static groupBy<T, TKey>(iterable: Iterable<T>, selectKey: (item: T) => TKey): Map<TKey, T[]> {
        const result = new Map<TKey, T[]>();
        for (const item of iterable) {
            const key = selectKey(item);
            const keyList = result.get(key);
            if (keyList == undefined) {
                result.set(key, [item]);
            } else {
                keyList.push(item);
            }
        }
        return result;
    }

    public static sortBy<T>(items: Iterable<T>, selector: (item: T) => string | number): T[] {
        const result = [...items];
        result.sort((aItem, bItem) => {
            const leftValue = selector(aItem);
            const rightValue = selector(bItem);
            if (leftValue === rightValue) {
                return 0;
            } else if (leftValue < rightValue) {
                return -1;
            } else {
                return 1;
            }
        });
        return result;
    }

    public static last<T>(items: MinLengthArray<T, 1>): T;
    public static last<T>(items: Iterable<T> | readonly T[]): undefined | T;
    public static last<T>(items: Iterable<T> | readonly T[]): undefined | T {
        if (Array.isArray(items)) {
            return items[items.length - 1];
        }
        let lastItem: T | undefined;
        for (const item of items) {
            lastItem = item;
        }
        return lastItem;
    }

    public static first<T>(items: [T, ...unknown[]]): T;
    public static first<T>(items: MinLengthArray<T, 1>): T;
    public static first<T>(items: Iterable<T> | readonly T[]): undefined | T;
    public static first<T>(items: Iterable<T> | readonly T[]): undefined | T {
        if (Array.isArray(items)) {
            return items[0];
        }
        let firstItem: T | undefined;
        for (const item of items) {
            firstItem = item;
            break;
        }
        return firstItem;
    }

    public static single<T>(items: readonly T[] | Iterable<T>, singleAssertMessage?: string): T {
        if (Array.isArray(items)) {
            if (items.length !== 1) {
                throw new Error(
                    singleAssertMessage ?? `Sequence must contain exactly single element, but got ${items.length}`
                );
            }
            return items[0] ?? reject();
        } else {
            let singleElement: T | typeof EmptySingleElement = EmptySingleElement;
            for (const item of items) {
                if (singleElement !== EmptySingleElement) {
                    throw new Error(
                        singleAssertMessage ?? `Sequence must contain exactly single element, but got at least 2`
                    );
                }
                singleElement = item;
            }
            if (singleElement === EmptySingleElement) {
                throw new Error(singleAssertMessage ?? "Sequence must contain exactly single element, but got 0");
            }
            return singleElement;
        }
    }

    public static *iterateEntries<T>(items: readonly T[] | T[] | Iterable<T>): Iterable<[number, T]> {
        let index = 0;
        for (const item of items) {
            yield [index, item];
            index++;
        }
    }

    public static *skipLast<T>(items: Iterable<T>, skipCount: number = 1): Iterable<T> {
        if (skipCount === 0) {
            yield* items;
            return;
        }
        const prevItems: Array<T | typeof EmptySingleElement> = new Array(skipCount).fill(EmptySingleElement);
        let resultItem: T | typeof EmptySingleElement = EmptySingleElement;
        let currentIndex = 0;
        for (const item of items) {
            // @ts-expect-error В общем, строка выше гарантирует длину массива
            resultItem = prevItems[currentIndex];
            prevItems[currentIndex] = item;
            currentIndex = (currentIndex + 1) % skipCount;
            if (resultItem != EmptySingleElement) {
                yield resultItem;
            }
        }
    }

    public static minBy<T>(items: readonly T[] | T[] | Iterable<T>, compareFunc: CompareFunc<T>): T {
        let minValue: typeof NoValueMarker | T = NoValueMarker;
        for (const item of items) {
            if (minValue === NoValueMarker) {
                minValue = item;
            } else if (compareFunc(item, minValue) === -1) {
                minValue = item;
            }
        }
        if (minValue === NoValueMarker) {
            throw new InvalidArgumentError("items", "Items should be not empty list");
        }
        return minValue;
    }

    public static minByOrUndefined<T>(
        items: readonly T[] | T[] | Iterable<T>,
        compareFunc: CompareFunc<T>
    ): T | undefined {
        let minValue: typeof NoValueMarker | T = NoValueMarker;
        for (const item of items) {
            if (minValue === NoValueMarker) {
                minValue = item;
            } else if (compareFunc(item, minValue) === -1) {
                minValue = item;
            }
        }
        if (minValue === NoValueMarker) {
            return undefined;
        }
        return minValue;
    }

    public static range(itemCount: number): Iterable<number>;
    public static range(start: number, itemCount: number): Iterable<number>;
    public static *range(inStartOrCount: number, inItemCount?: number): Iterable<number> {
        const start = inItemCount === undefined ? 0 : inStartOrCount;
        const itemCount = inItemCount === undefined ? inStartOrCount : inItemCount;
        if (itemCount < 0) {
            throw new InvalidArgumentError("itemCount", "itemCount must be a non negative number");
        }
        for (let i = start; i < start + itemCount; i++) {
            yield i;
        }
    }

    /**
     * @summary Вставляет между элементами массива заданный элемент и возвращает новый массив
     */
    public static intersperse<TI, TS>(items: TI[], item: TS): Array<TI | TS> {
        return this.intersperseFn(items, () => item);
    }

    /**
     * @summary Вставляет между элементами массива элемент, создаваемый функцией, и возвращает новый массив
     */
    public static intersperseFn<TI, TS>(items: TI[], item: (nextItem: TI, index: number) => TS): Array<TI | TS> {
        return items.flatMap((x, index) => (index === 0 ? [x] : [item(x, index), x]));
    }

    public static zip<T1, T2>(items1: Iterable<T1>, items2: Iterable<T2>): Iterable<[T1 | undefined, T2 | undefined]>;
    public static *zip(...iterables: Array<Iterable<unknown>>): Iterable<unknown[]> {
        const iterators: Array<Iterator<unknown> | typeof NoValueMarker> = iterables.map(it => Iterator.from(it));
        let hasUndoneIterators = true;
        while (hasUndoneIterators) {
            const resultEntry = [];
            hasUndoneIterators = false;
            for (const [i, iterator] of this.iterateEntries(iterators)) {
                if (iterator === NoValueMarker) {
                    resultEntry.push(undefined);
                } else {
                    const resultValue = iterator.next();
                    if (resultValue.done) {
                        resultEntry.push(undefined);
                        iterators[i] = NoValueMarker;
                    } else {
                        resultEntry.push(resultValue.value);
                        hasUndoneIterators = true;
                    }
                }
            }
            if (hasUndoneIterators) {
                yield resultEntry;
            }
        }
    }

    public static *distinctBy<T>(items: Iterable<T>, idSelector: (item: T) => string): Iterable<T> {
        const set = new Set<string>();
        for (const item of items) {
            const id = idSelector(item);
            if (!set.has(id)) {
                set.add(id);
                yield item;
            }
        }
    }

    public static reduce<T, U>(
        items: Iterable<T>,
        reducer: (accumulator: U, item: T, index: number) => U,
        initialValue: U
    ): U {
        let index = 0;
        return Iterator.from(items).reduce((acc, item) => reducer(acc, item, index++), initialValue);
    }

    public static *batch<T>(items: Iterable<T>, batchSize: number): Iterable<Iterable<T>> {
        const iterator = Iterator.from(items);
        const safeBatchSize = batchSize > 0 ? batchSize : 1;
        let done = false;
        while (!done) {
            const batch: T[] = [];
            while (batch.length < safeBatchSize) {
                const current = iterator.next();
                if (current.done) {
                    done = true;
                    break;
                } else {
                    batch.push(current.value);
                }
            }
            if (batch.length > 0) {
                yield batch;
            }
        }
    }
}
